import { useEffect, useState } from "react";
import { subscribeInstallable, triggerInstall, isStandalone, isIOS } from "@/lib/pwa";
import { Button } from "@/components/ui/button";
import { X, Download, Share2 } from "lucide-react";

const DISMISS_KEY = "aab_install_dismissed_at";
const DISMISS_HOURS = 72;

export default function InstallPWA() {
  const [installable, setInstallable] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // já instalado
    const raw = localStorage.getItem(DISMISS_KEY);
    if (raw) {
      const ageH = (Date.now() - Number(raw)) / 3600_000;
      if (ageH < DISMISS_HOURS) {
        setDismissed(true);
        return;
      }
    }
    const unsub = subscribeInstallable((v) => setInstallable(v));
    // iOS não emite beforeinstallprompt — mostra dica após 4s
    if (isIOS()) {
      const t = setTimeout(() => setShowIOSHint(true), 4000);
      return () => { clearTimeout(t); unsub(); };
    }
    return unsub;
  }, []);

  const close = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const install = async () => {
    const r = await triggerInstall();
    if (r?.outcome === "accepted") setDismissed(true);
  };

  if (dismissed || isStandalone()) return null;

  // Android/Chrome/Edge — prompt disponível
  if (installable) {
    return (
      <div
        className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-40 rounded-xl border border-border bg-card shadow-lg p-4 flex items-start gap-3"
        data-testid="pwa-install-banner"
      >
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Download className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Instalar AAB Repairs</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Instale o app na tela inicial para acesso rápido e uso em tela cheia.
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={install} data-testid="pwa-install-btn">
              Instalar
            </Button>
            <Button size="sm" variant="outline" onClick={close} data-testid="pwa-install-dismiss">
              Agora não
            </Button>
          </div>
        </div>
        <button onClick={close} className="text-muted-foreground hover:text-foreground -mt-1 -mr-1 p-1" aria-label="Fechar">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // iOS — instrução manual
  if (showIOSHint) {
    return (
      <div
        className="fixed bottom-20 left-4 right-4 z-40 rounded-xl border border-border bg-card shadow-lg p-4 flex items-start gap-3"
        data-testid="pwa-ios-hint"
      >
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Share2 className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Instalar no iPhone</div>
          <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            Toque em <b>Compartilhar</b> <span className="inline-block px-1">⎋</span> no Safari e escolha
            <b> &ldquo;Adicionar à Tela de Início&rdquo;</b>.
          </div>
        </div>
        <button onClick={close} className="text-muted-foreground hover:text-foreground -mt-1 -mr-1 p-1" aria-label="Fechar">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return null;
}
